import React, { useRef, useEffect, useState } from 'react'

import './PasihyppelyPage.scss'
import { BackButton } from '../../components/BackButton'
import { SEO } from '../../components/SEO'
import {
  handleJump,
  startGame,
  cancel,
  getHighScore,
  getLastScore,
  newRecordAchieved,
  clearNewRecord,
  handleVisibilityChange,
} from './mechanics'
import { resize } from './renderer'
import pasiSprite from '../../assets/pasi.png'

const handlePointer = (gameStateRef: React.RefObject<'menu' | 'playing' | 'gameover'>) => {
  handleJump(gameStateRef)
}
const handleKey = (
  e: KeyboardEvent,
  gameStateRef: React.RefObject<'menu' | 'playing' | 'gameover'>
) => {
  if (e.code === 'Space') {
    e.preventDefault()
    handleJump(gameStateRef)
  }
}

export const PasihyppelyPage: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'gameover'>('menu')
  const gameStateRef = useRef<'menu' | 'playing' | 'gameover'>(gameState)
  const [bestScore, setBestScore] = useState<number>(() => getHighScore())
  const [score, setScore] = useState<number>(0)
  const [lastScore, setLastScore] = useState<number>(() => getLastScore())
  const [isNewRecord, setIsNewRecord] = useState(false)

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]')
    if (meta) {
      const originalContent = meta.getAttribute('content')
      meta.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      )

      return () => {
        if (originalContent) {
          meta.setAttribute('content', originalContent)
        }
      }
    }
    return () => {
      // no op
    }
  }, [])

  const onStart = () => {
    setScore(0)
    setGameState('playing')
    startGame(canvasRef, gameStateRef, setGameState, setScore)
  }

  useEffect(() => {
    gameStateRef.current = gameState
    if (gameState !== 'playing') {
      setBestScore(getHighScore())
      setLastScore(getLastScore())
      setIsNewRecord(newRecordAchieved)
      clearNewRecord()
    }
  }, [gameState])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return
    }

    ctx.imageSmoothingEnabled = false

    const handlePointerLocal = () => {
      handlePointer(gameStateRef)
    }
    const handleKeyLocal = (e: KeyboardEvent) => {
      handleKey(e, gameStateRef)
    }

    window.addEventListener('pointerdown', handlePointerLocal)
    window.addEventListener('keydown', handleKeyLocal)
    const resizeListener = () => {
      resize(canvas)
    }
    window.addEventListener('resize', resizeListener)
    const visibilityListener = () => {
      handleVisibilityChange(document.visibilityState, gameStateRef, setGameState)
    }
    document.addEventListener('visibilitychange', visibilityListener)

    return () => {
      cancel()
      window.removeEventListener('pointerdown', handlePointerLocal)
      window.removeEventListener('keydown', handleKeyLocal)
      window.removeEventListener('resize', resizeListener)
      document.removeEventListener('visibilitychange', visibilityListener)
    }
  }, [])

  return (
    <div className="pasihyppelyPage">
      <SEO
        title="Pasihyppely | Aamukasaaja"
        description="Pasihyppely on varusmiehen tärkein peli jonka avulla opit väistelemään vihollisen miinoja."
        keywords="aamukasa, tj, laskuri, intti, asepalvelus, inttipeli"
        canonical="https://aamukasaaja.fi/pasihyppely"
      />
      <div className="header">
        <BackButton to="/" onClick={cancel} />
        {gameState !== 'playing' && <h2>Pasihyppely</h2>}
        {gameState === 'playing' && <div className="currentScore">Pisteet: {score}</div>}
      </div>
      {gameState !== 'playing' && (
        <div className="menu">
          <div className="menuHeader">
            <img src={pasiSprite} alt="Pasi" className="menuSprite" />
            <h1>Pasihyppely</h1>
            <img src={pasiSprite} alt="Pasi" className="menuSprite mirror" />
          </div>
          <div className="scoreBoard">
            <div className={`bestScore${isNewRecord ? ' newRecord' : ''}`}>
              {isNewRecord ? `Uusi ennätys: ${bestScore}` : `Ennätys: ${bestScore}`}
            </div>
            {gameState === 'gameover' && <div className="lastScore">Tulos: {lastScore}</div>}
          </div>
          <button type="button" onClick={onStart}>
            {gameState === 'menu' ? 'Aloita' : 'Aloita uudelleen'}
          </button>
        </div>
      )}
      <canvas ref={canvasRef} />
    </div>
  )
}
